import { qfRestGet } from './qfClient';

/**
 * Options-tape figures for the index tiles on `/market`.
 *
 * **LIVE** — QuantForge `GET /v1/steamroom/watchlist-quotes?symbols=`.
 *
 * ⚠️ **A general-purpose endpoint we consume a single field of.** It carries
 * quotes, volumes, IV/HV term structure and next earnings; the tiles want
 * `pressure` and nothing else. QF declined to build a trimmed variant for four
 * tiles, which is the right call — the extra fields cost one response, not one
 * request each.
 *
 * **Batch by design**: up to 64 symbols, comma-separated, each ≤12 chars; 422
 * outside that. One request covers all four tiles, and a per-symbol version
 * would multiply every page load by four.
 */

/**
 * QF's row. Every field is nullable — a symbol with no data comes back as an
 * all-null row rather than being omitted, so the array always matches the
 * request.
 */
interface WatchlistRow {
  symbol: string;
  net_call_premium: number | null;
  net_put_premium: number | null;
  /**
   * Canonical directional net premium — **this is our `netPremium`**.
   *
   * QF writes it as `(call_ask + put_bid) − (call_bid + put_ask)`; we defined
   * it as `net_call_premium − net_put_premium`. The two regroup to the same
   * expression, verified to the dollar across all four symbols. No
   * reconciliation is owed on either side.
   *
   * ⚠️ There is a THIRD formula that looks similar and is not this one:
   * `(call_ask + put_ask) − (call_bid + put_bid)` is a buy/sell aggression
   * gauge, and it counts put *buying* as positive rather than bearish. Never
   * "simplify" toward it.
   */
  pressure: number | null;
  last: number | null;
  change: number | null;
  change_pct: number | null;
  session_phase: string | null;
  call_volume: number | null;
  put_volume: number | null;
  volume: number | null;
  iv30: number | null;
  iv60: number | null;
  iv90: number | null;
  hv10: number | null;
  hv20: number | null;
  hv30: number | null;
  next_earnings: string | null;
}

interface WatchlistQuotesResponse {
  rows: WatchlistRow[];
}

export interface IndexStats {
  /**
   * Net options premium, signed. Positive is call-side buying.
   *
   * ⚠️ Null when either leg is missing. Treating an absent leg as zero would
   * report a confident number built from half the tape.
   *
   * ⚠️ **QF currently returns `0.0` rather than null for an absent leg** — their
   * storage holds the two legs as non-nullable sums, so a name with no put
   * trades yields zero and `pressure` reads flat. They agree this is wrong and
   * have a fix in flight using per-leg volume as a presence witness. Harmless
   * for SPY/QQQ/DIA/IWM, which always trade both sides; it would matter if this
   * endpoint were ever pointed at something illiquid.
   */
  netPremium: number | null;
}

/** Keyed by uppercase symbol. */
export type IndexStatsBySymbol = Record<string, IndexStats>;

/** The four index ETFs the rail shows. */
export const INDEX_SYMBOLS = ['SPY', 'QQQ', 'DIA', 'IWM'] as const;

const WATCHLIST_QUOTES_PATH = '/v1/steamroom/watchlist-quotes';

/**
 * Net premium for the four index ETFs, keyed by symbol.
 *
 * Throws rather than returning an empty map: a missing figure renders as a
 * dash, and a dash caused by a failed request is indistinguishable from one
 * caused by a genuinely flat tape.
 */
export async function fetchIndexStats(): Promise<IndexStatsBySymbol> {
  const body = await qfRestGet<WatchlistQuotesResponse>(WATCHLIST_QUOTES_PATH, {
    symbols: INDEX_SYMBOLS.join(','),
  });
  const rows = body?.rows ?? [];
  return Object.fromEntries(
    rows.map((r) => [r.symbol.toUpperCase(), { netPremium: r.pressure ?? null }])
  );
}
